import React from "react";

// External
import styled, { css } from "styled-components";
import { StaticImage } from "gatsby-plugin-image";
import {
  FiFacebook,
  FiTwitter,
  FiLinkedin,
  FiLink,
  FiChevronLeft,
} from "react-icons/fi";
import { navigate } from "gatsby";

// Internal
import useMediaQuery from "../../hooks/use-media-query";
import IconButton from "../../components/form/IconButton";
import { isVideo } from "../../utils/helpers/file";
import { Flex } from "../../styles/global-styles";
import {
  fontFamily,
  colors,
  breakpoints,
  getProjectTheme,
  sectionRhythm,
} from "../../../theme";
import { safeNavigator } from "../../utils/helpers/ssr";
import { PROJECT_CONDEXO_PAY } from "../../utils/constants/projects";
import ContactMeSection from "../contact-me-section";
import { ProjectHero, caseStudyBodyStyles } from "./project-page-media";
import Testimonials from "../reviews-section/Testimonials";

const projectTheme = getProjectTheme("condexo-pay");

// Styles

const hasClipboard = safeNavigator.clipboard !== undefined;

const BlogContainer = styled.div`
  width: calc(100vw - 48px);
  padding-top: ${sectionRhythm.paddingY.mobile}px;
  margin: 0 auto;

  img,
  video {
    width: 100%;
    height: auto;
  }

  @media only screen and (min-width: ${breakpoints.tablet}) {
    width: 65%;
    max-width: 800px;
    padding-top: ${sectionRhythm.paddingY.desktop}px;
    margin: 0 auto;
  }
  @media only screen and (min-width: 900px) {
    width: 60%;
  }
`;

const BlogTitle = styled.h1`
  margin: 0 0 16px;
  font-family: ${fontFamily.extraBold};
  font-size: 24px;
  // font-size: 28px;
  // width: 90%;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 44px;
    margin: 0 0 32px;
  }
`;
const BlogHelperText = styled.span`
  color: ${colors.grey.darkMedium};
  // margin-right: 12px;
`;
const Video = styled.video`
  //   width: 100%;
  height: 102%;
  border-radius: 6px !important;
  transform: scale(1.02);
  // box-shadow: 2px 2px 12px rgba(0, 0, 0, 0.1);
  @media only screen and (min-width: ${breakpoints.tablet}) {
    border-radius: 12px;
  }
`;

const VideoContainer = styled.div`
  width: ${(props) => (props.isMobileCover ? "96%" : "100%")};
  border: 4px solid white;
  border-radius: 6px !important;
  overflow: hidden;
  box-sizing: border-box;
  background-image: linear-gradient(180deg, ${colors.white}, ${colors.white});
  box-shadow: 17px 17px 48px 0 rgb(0 0 0 / 12%);
  margin-top: 1.5rem;
`;

const VideoContainerMobile = styled(VideoContainer)`
  z-index: 2;
  position: absolute;

  width: 70px;
  height: 135.516px;
  right: 0px;
  bottom: -20px;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    width: 120px;
    height: 248.203px;
    right: -15px;
    bottom: -30px;
  }
`;

const HeroCoverSection = styled.div`
  position: relative;
  margin-top: 1.5rem;
`;

const BlogContentContainer = styled.div`
  ${caseStudyBodyStyles}
`;
const Name = styled.span`
  color: ${colors.grey.darkMedium};
`;

const SubTitleContainer = styled(Flex)`
  padding: 0;
`;

const BlogFooter = styled.div``;
const ShareBlogFooter = styled(Flex)`
  border-bottom: 1px solid ${colors.grey.medium};
  padding: 20px 13px 8px;
  margin-top: 36px;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    padding: 30px 20px;
    margin-top: 100px;
  }
`;

const OverviewSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 2rem;
  margin-bottom: 0;
`;

const OverviewItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  @media only screen and (min-width: 1000px) {
    flex-direction: row;
  }
`;

const OverviewItem = styled.div`
  margin-bottom: 1rem;

  ${(props) =>
    props.isSeventyPercent &&
    css`
      width: 100%;
      @media only screen and (min-width: 1000px) {
        width: 70%;
      }
    `}
  ${(props) =>
    props.isThirtyPercent &&
    css`
      width: 100%;
      @media only screen and (min-width: 1000px) {
        width: 30%;
      }
    `}
`;
const OverviewLabel = styled.div`
  font-size: 14px;
  color: ${colors.grey.darkMedium};
  margin-bottom: 4px;
`;
const OverviewValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;

  a,
  &[href] {
    color: inherit;
    word-break: break-all;

    &:hover {
      color: ${colors.linkHover.light};
      text-decoration: underline;
    }
  }
`;

const GalleryContentContainer = styled.div`
  margin-block: 2rem;
`;

const GalleryContentItem = styled.div`
  margin-block: 2rem;
  position: relative;
`;

const VideoCaption = styled.span`
  font-size: 0.8rem;
  text-align: center;
  width: 100%;
  display: inline-block;
  margin-top: 0.8rem;
`;

const CondexoPayProjectSection = () => {
  const isMobile = !useMediaQuery(`(min-width: ${breakpoints.tablet} )`);

  const goBack = () => {
    navigate(-1);
  };

  const randomData = {
    date: new Date(),
    title: "Simplifying & securing online payment through all-in-one app",
    clientName: "Condexo",
    projectName: "Condexo Pay",
    role: "React developer",
    year: "2019-2020",
    cover_src:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648038674/projects/condexo-pay/condexo-pay__landing-page--old_zxl90m_gq9t2z.mp4",
    mobile_cover_src:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648038638/projects/condexo-pay/condex-pay__landing-page--mobile_dfw2r8_mpvl3n.mp4",
    clientCountry: "Italy",
    servicesProvided: "Front-end development",
    industry: "FinTech",
    techStack: [
      "Javascript",
      "React",
      "Redux",
      "Redux Saga",
      "Material UI",
      "Stripe",
    ],
    projectLink: "https://pay.condexo.it/",
    clientReviews: [
      {
        name: "Elisa Fiorenzani",
        position: "UX/UI designer",
        company: "Condexo",
        profilePic: "elisa-condexo.jpeg",
        text: "It was a pleasure to work with Parmeet, an honest and reliable Dev!",
      },
    ],
  };

  const mediaLinks = {
    myPayments:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648038599/projects/condexo-pay/condexo-pay__my-payments_crskpm_xlqpif.mp4",
    bolletini:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648038634/projects/condexo-pay/condexo-pay__bolettini-flow_rmytnt_vn8uf3.mp4",
    giftCard:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648038626/projects/condexo-pay/condexo-pay__gift-card_nmzwxf_fqtwhi.mp4",
  };

  return (
    <>
      <BlogContainer>
        {isMobile && (
          <Flex spaceBetween>
            <IconButton
              bgColor="#bbb"
              iconColor="#000"
              style={{ marginLeft: "-12px" }}
              onClick={goBack}
              ariaLabel="Go back"
            >
              <FiChevronLeft />
            </IconButton>
            {/* <SocialMediaButtons shareLinks={shareLinks} isMobile={true} /> */}
          </Flex>
        )}
        <BlogTitle>{randomData.title}</BlogTitle>
        <SubTitleContainer spaceBetween>
          <Flex>
            <Name>Role: {randomData.role}</Name>
          </Flex>
          {!isMobile && (
            <Flex>
              <BlogHelperText>Year: {randomData.year}</BlogHelperText>
            </Flex>
          )}
        </SubTitleContainer>
        <HeroCoverSection>
          <ProjectHero
            src={PROJECT_CONDEXO_PAY.media.hero}
            alt={randomData.title}
          />
        </HeroCoverSection>
        <OverviewSection>
          <OverviewItemContainer>
            <OverviewItem isSeventyPercent>
              <OverviewLabel>Client</OverviewLabel>
              <OverviewValue>{randomData.clientName}</OverviewValue>
            </OverviewItem>
            <OverviewItem isThirtyPercent>
              <OverviewLabel>Country</OverviewLabel>
              <OverviewValue>{randomData.clientCountry}</OverviewValue>
            </OverviewItem>
          </OverviewItemContainer>
          <OverviewItemContainer>
            <OverviewItem isSeventyPercent>
              <OverviewLabel>Services provided</OverviewLabel>
              <OverviewValue>{randomData.servicesProvided}</OverviewValue>
            </OverviewItem>
            <OverviewItem isThirtyPercent>
              <OverviewLabel>Industry</OverviewLabel>
              <OverviewValue>{randomData.industry}</OverviewValue>
            </OverviewItem>
          </OverviewItemContainer>
          {isMobile && (
            <OverviewItem isHundredPercent>
              <OverviewLabel>Year</OverviewLabel>
              <OverviewValue>{randomData.year}</OverviewValue>
            </OverviewItem>
          )}
          <OverviewItem isHundredPercent>
            <OverviewLabel>Tech stack</OverviewLabel>
            <OverviewValue>{randomData.techStack.join(", ")}</OverviewValue>
          </OverviewItem>
          {randomData.projectLink ? (
            <OverviewItem>
              <OverviewLabel>Project link</OverviewLabel>
              <OverviewValue>
                <a
                  href={randomData.projectLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {randomData.projectLink}
                </a>
              </OverviewValue>
            </OverviewItem>
          ) : null}
        </OverviewSection>
        <BlogContentContainer>
          <section>
            <h4>Challenge</h4>
            <p>
              Making payments to various basic services was a hectic and
              tiresome process. One had to stay in queues to pay their home
              installments, water bills, electricity bills, gas bills, etc.
              Thus, A unified platform was to be built to streamline the payment
              process. In addition to that, various other services like buying a
              gift card, recharging a mobile phone had to be integrated as well.
              Moreover, the platform had to ensure the safety and security of
              the payee.
            </p>
            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.myPayments} />
            </VideoContainer>
          </section>
          <section>
            <h4>Process</h4>
            <p>
              The project began with meetings with various stakeholders in order
              to get a better understanding of the project. Once the project's
              requirements and scope were clear, we proceeded further to review
              the designs, which were then created by the client's in-house UI
              designer. After reviewing the designs a better visual idea of the
              project was there with us. So, with my team comprising a backend
              developer and a project manager proceeded further with planning on
              selecting the most appropriate technology that could suit the
              requirement of the client. After a thorough round of discussion,
              for the front-end part, we decided to proceed with the bare
              minimum boilerplate code for React in order to keep the codebase
              as light as possible and add packages as the demand arises. Stripe
              was the client's payment service of choice, which was integrated
              for all the payment operations of the web app. The project had
              requirements for various custom components to be built, which were
              created by leveraging Material UI's component library. All the
              components were tested for performance and accuracy since they had
              to handle thousands of records containing sensitive data.
              Throughout the development process, Agile development was followed
              by having sprints of a couple of weeks and utilizing tools like
              Jira and Basecamp. On successful completion of development, the
              code was verified by the client's development team and after some
              suggested changes, the code was later pushed to production.
            </p>
            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.bolletini} />
            </VideoContainer>
          </section>
          <section>
            <h4>Outcome</h4>
            <p>
              A responsive, fast, and safe web application was delivered, which
              resulted in our client being able to successfully launch the MVP
              version of the website and begin providing a better way for
              payments for the people of Italy.
            </p>
            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.giftCard} />
            </VideoContainer>
          </section>

          <section>
            <h4>Testimonials</h4>
            <Testimonials
              showMore={false}
              reviews={PROJECT_CONDEXO_PAY.client.reviews}
            />
          </section>
          {/*     <br />
        <br />
        <br />
        <br />
        <br />
        <h4>Gallery</h4>
        <GalleryContentContainer>
          <GalleryContentItem>
            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.giftCard} />
            </VideoContainer>
            <VideoCaption>My payments</VideoCaption>
          </GalleryContentItem>
          <GalleryContentItem>
            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.giftCard} />
            </VideoContainer>
          </GalleryContentItem>
        </GalleryContentContainer> */}
        </BlogContentContainer>
      </BlogContainer>
      <ContactMeSection />
    </>
  );
};

export default CondexoPayProjectSection;

/* DONE: Header Component */
/* TODO: Cover Image */
/* TODO: Project overview Section */
/* TODO: Challenge Section */
/* TODO: Process Section */
/* TODO: Outcome Section */
/* TODO: Client review Section */
/* TODO: Gallery Section */
/* TODO: Add link to right of project name */
/* TODO: Add icons for Role Section */
